import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Directive, InjectionToken, Injectable, NgModule, forwardRef, Input, TemplateRef, ContentChild } from '@angular/core';
import * as i1 from '@angular/forms';
import { NgControl, FormGroup, FormArray, FormControl, FormsModule, ReactiveFormsModule, FormGroupDirective, NG_VALUE_ACCESSOR } from '@angular/forms';

class SeFeFormControlElementDirective {
    constructor(controlDir, host) {
        this.controlDir = controlDir;
        this.host = host;
    }
    ngOnInit() {
        this.control.nativeElement = this.host.nativeElement;
    }
    get control() {
        return this.controlDir.control;
    }
}
SeFeFormControlElementDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeFeFormControlElementDirective, deps: [{ token: i1.NgControl }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
SeFeFormControlElementDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: SeFeFormControlElementDirective, selector: "[formControl], [formControlName]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeFeFormControlElementDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[formControl], [formControlName]'
                }]
        }], ctorParameters: function () { return [{ type: i1.NgControl }, { type: i0.ElementRef }]; } });

const SE_FE_FORM_FIELD_INPUT = new InjectionToken('SeFeFormFieldInput');
let idCounter = 1;
class SeFeFormService {
    constructor(formBuilder) {
        this.formBuilder = formBuilder;
        // noop
    }
    // Reset control ID on changes, using a default if the customId is not present
    setId(control, customId) {
        if (customId && customId === control.id)
            return;
        control.id = customId || control.id || `FormControl${this.nextId()}`;
    }
    validate(form) {
        if (this.isValid(form))
            return true;
        this.setErrorFocus(form);
        return false;
    }
    isRequired(control) {
        const validator = control && control.validator && control.validator({}) || {};
        return validator.required;
    }
    controlErrors(control, submitted) {
        if (!(control instanceof NgControl)) {
            control = control.control ?? control;
        }
        if (submitted === undefined)
            submitted = control._parent.submitted; // access private property
        const controlInvalid = control && control.invalid;
        const invalidAndTouchedAndDirty = controlInvalid && control.touched && control.dirty;
        const invalidAndSubmitted = controlInvalid && submitted;
        return invalidAndTouchedAndDirty || invalidAndSubmitted ? Object.keys(control.errors) : [];
    }
    isValid(form) {
        this.revalidateAll(form);
        form.markAllAsTouched();
        this.markAllAsDirty(form);
        return !form.invalid;
    }
    revalidateAll(ac) {
        this.formControls(ac).forEach(c => this.revalidateAll(c));
        ac.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }
    setErrorFocus(form) {
        const errorFormControl = [];
        const iterateForm = (obj) => {
            Object.keys(obj).forEach(key => {
                const abstractControl = obj[key];
                if (abstractControl instanceof FormGroup || abstractControl instanceof FormArray) {
                    iterateForm(abstractControl.controls);
                }
                else if (abstractControl instanceof FormControl && abstractControl.errors !== null) {
                    errorFormControl.push(abstractControl);
                }
            });
        };
        iterateForm(form.controls);
        const sortedElements = this.sortFormElements(errorFormControl.map(fc => fc.nativeElement));
        this.focusFirstErrorEl(sortedElements[0]);
    }
    markAllAsDirty(form) {
        this.formControls(form).forEach(control => this.markAllAsDirty(control));
        form.markAsDirty({ onlySelf: true });
    }
    formControls(form) {
        const { controls } = form;
        return controls ? Object.values(controls) : [];
    }
    nextId() {
        return idCounter++;
    }
    focusFirstErrorEl(el) {
        if (!el)
            return;
        const selectors = 'input, textarea, area, select, contenteditable';
        if (el.matches(selectors)) {
            el.focus();
        }
        else if (el.querySelector(selectors)) {
            el.querySelector(selectors).focus();
        }
        else {
            el.focus();
        }
    }
    sortFormElements(array) {
        return array.sort(function (a, b) {
            if (a === b)
                return 0;
            if (!a.compareDocumentPosition)
                return a.sourceIndex - b.sourceIndex;
            if (a.compareDocumentPosition(b) & 2)
                return 1;
            return -1;
        });
    }
}
SeFeFormService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeFeFormService, deps: [{ token: i1.FormBuilder }], target: i0.ɵɵFactoryTarget.Injectable });
SeFeFormService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeFeFormService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeFeFormService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.FormBuilder }]; } });

class SeFeFormServiceModule {
}
SeFeFormServiceModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeFeFormServiceModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SeFeFormServiceModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: SeFeFormServiceModule, declarations: [SeFeFormControlElementDirective], imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule], exports: [FormsModule,
        ReactiveFormsModule,
        SeFeFormControlElementDirective] });
SeFeFormServiceModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeFeFormServiceModule, providers: [
        SeFeFormService,
    ], imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule, FormsModule,
        ReactiveFormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeFeFormServiceModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        SeFeFormControlElementDirective
                    ],
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                    ],
                    exports: [
                        FormsModule,
                        ReactiveFormsModule,
                        SeFeFormControlElementDirective,
                    ],
                    providers: [
                        SeFeFormService,
                    ],
                }]
        }] });

class SeFeFormControlDirective {
    constructor(injector, formService) {
        this.injector = injector;
        this.formService = formService;
        this.control = this.injector.get(NgControl);
        this.form = this.injector.get(FormGroupDirective, { skipSelf: true });
        if (this.control !== null) {
            this.control.valueAccessor = this;
        }
    }
    inputChange($event) {
        this.onChange($event.currentTarget.value);
    }
    touched($event) {
        this.onTouched($event.currentTarget.touched);
    }
    writeValue(value) {
        this._value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    get formBuilder() {
        return this.formService.formBuilder;
    }
    get formControl() {
        const { control } = this;
        if (!control)
            return;
        // Test setup uses `this.control.control`. I couldn't figure out how to mock the parent relationship correctly.
        if (control.control)
            return control.control;
        // The formControlName API says .control should return the formControl, but is does not, so we look it up from the _parent.
        return control._parent.control.controls[control.name];
    }
    ngDoCheck() {
        this.formService.setId(this, this.seFeDataId);
        this.errors = this.formService.controlErrors(this.control, this.form.submitted);
    }
}
SeFeFormControlDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeFeFormControlDirective, deps: [{ token: i0.Injector }, { token: SeFeFormService }], target: i0.ɵɵFactoryTarget.Directive });
SeFeFormControlDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: SeFeFormControlDirective, inputs: { seFeDataId: "seFeDataId", seFeDataLabel: "seFeDataLabel" }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SeFeFormControlDirective),
            multi: true,
        }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeFeFormControlDirective, decorators: [{
            type: Directive,
            args: [{
                    providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => SeFeFormControlDirective),
                            multi: true,
                        }],
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: SeFeFormService }]; }, propDecorators: { seFeDataId: [{
                type: Input
            }], seFeDataLabel: [{
                type: Input
            }] } });

class SeFeFormSelectControlDirective {
    constructor(renderer, elementRef, injector, formService) {
        this.renderer = renderer;
        this.elementRef = elementRef;
        this.injector = injector;
        this.formService = formService;
        // noop
    }
    inputChange($event) {
        this._value = $event.currentTarget.value;
        this.onChange(this._value);
    }
    touched($event) {
        this.onTouched($event.currentTarget.touched);
    }
    writeValue(value) {
        this._value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    ngOnInit() {
        this.control = this.control || this.injector.get(NgControl);
        if (this.control !== null) {
            this.control.valueAccessor = this;
        }
    }
    ngDoCheck() {
        this.formService.setId(this, this.seFeDataId);
        this.errors = this.formService.controlErrors(this.control);
    }
}
SeFeFormSelectControlDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeFeFormSelectControlDirective, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }, { token: i0.Injector }, { token: SeFeFormService }], target: i0.ɵɵFactoryTarget.Directive });
SeFeFormSelectControlDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: SeFeFormSelectControlDirective, inputs: { seFeDataId: "seFeDataId", seFeDataLabel: "seFeDataLabel" }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SeFeFormSelectControlDirective),
            multi: true,
        }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeFeFormSelectControlDirective, decorators: [{
            type: Directive,
            args: [{
                    providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => SeFeFormSelectControlDirective),
                            multi: true,
                        }],
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ElementRef }, { type: i0.Injector }, { type: SeFeFormService }]; }, propDecorators: { seFeDataId: [{
                type: Input
            }], seFeDataLabel: [{
                type: Input
            }] } });

class SeFeFormOptionsControlDirective {
    constructor(injector, seFeFormService) {
        this.injector = injector;
        this.seFeFormService = seFeFormService;
        this.seFeDataOptions = [];
        this.seFeDataSpacious = false;
        this.required = false;
        this.checkedIndices = [];
        this.disabledIndices = [];
        //noop
    }
    ngOnInit() {
        this.control = this.injector.get(NgControl);
        this.form = this.injector.get(FormGroupDirective, { skipSelf: true });
        if (this.control !== null) {
            this.control.valueAccessor = this;
        }
        this.reset();
    }
    ngDoCheck() {
        this.seFeFormService.setId(this, this.seFeDataId);
        this.errors = this.seFeFormService.controlErrors(this.control, this.form.submitted);
    }
    touched($event) {
        this.onTouched($event.currentTarget.touched);
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    ngOnChanges() {
        this.reset();
    }
    getTemplate(config, templates) {
        const key = Object.keys(config)[0];
        const value = Object.values(config)[0];
        if (templates.length === 0)
            return null;
        if (!this.seFeDataOptions.some(o => o[key]))
            return templates.first?.templateRef;
        else
            return value ? templates.find((t) => t.templateName === value)?.templateRef : null;
    }
    get formBuilder() {
        return this.seFeFormService.formBuilder;
    }
    get options() {
        return this.seFeDataOptions;
    }
    get disabledValues() {
        let disabledOptions;
        if (this.seFeDataDisabled === true) {
            disabledOptions = [].concat(this.options || []);
        }
        else {
            disabledOptions = this.seFeDataDisabled || [];
        }
        return disabledOptions.map(option => this.optionValue(option));
    }
    get valueKey() {
        return this.seFeDataValueKey || 'value';
    }
    name(option) {
        const suffix = this.allowMultiple ? `_${this.optionValue(option).toString().replace(/[^a-z0-9]/gi, '_')}` : '';
        return `${this.formControlName}${suffix}`;
    }
    changeInput(checked, index) {
        // Sync checbox values
        if (this.allowMultiple) {
            this.checkedIndices[index] = checked;
            this.value = this.options
                .filter((option, i) => this.checkedIndices[i])
                .map(option => this.optionValue(option));
        }
        // Set radio value
        else {
            this.checkedIndices.length = 0;
            this.checkedIndices[index] = checked;
            this.value = checked ? this.optionValue(this.options[index]) : false; // TODO: verify this is ok, or allow for customizing
        }
    }
    focus() {
        this.focused = true;
    }
    blur(e) {
        // currentTarget gets cleared during the timeout and the SeFormControl logic fails
        const proxyEvent = { currentTarget: e.currentTarget };
        this.focused = false;
        setTimeout(() => this.focused || this.touched(proxyEvent));
    }
    click(e, i) {
        if (this.disabledIndices[i])
            e.preventDefault();
    }
    writeValue(value) {
        this._value = this.allowMultiple ? value : [value];
        if (this.allowMultiple) {
            this.options.forEach((opt, index) => {
                this.checkedIndices[index] = value && value.includes(opt[this.valueKey]);
            });
        }
        // Set radio value
        else {
            this.checkedIndices.length = 0;
            this.options.forEach((opt, index) => {
                this.checkedIndices[index] = opt[this.valueKey] === value || undefined;
            });
        }
    }
    // PRIVATE
    reset() {
        if (!this.formControl)
            return;
        this.required = this.seFeFormService.isRequired(this.formControl);
        this.checkedIndices = this.options.map(option => this.valueArray.includes(this.optionValue(option)));
        this.disabledIndices = this.options.map(option => this.disabledValues.includes(this.optionValue(option)));
    }
    get valueArray() {
        return [].concat(this.formControl.value || []);
    }
    get value() {
        return this.allowMultiple ? this._value : this._value[0];
    }
    set value(value) {
        this.writeValue(value);
        this.onChange(value);
    }
    get formControl() {
        const { control } = this;
        if (!control)
            return;
        // Test setup uses `this.control.control`. I couldn't figure out how to mock the parent relationship correctly.
        if (control.control)
            return control.control;
        // The formControlName API says .control should return the formControl, but is does not, so we look it up from the _parent.
        return control._parent.control.controls[control.name];
    }
    optionValue(option) {
        return typeof option === 'object' ? option[this.valueKey] : option;
    }
}
SeFeFormOptionsControlDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeFeFormOptionsControlDirective, deps: [{ token: i0.Injector }, { token: SeFeFormService }], target: i0.ɵɵFactoryTarget.Directive });
SeFeFormOptionsControlDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: SeFeFormOptionsControlDirective, inputs: { formControlName: "formControlName", seFeDataOptions: "seFeDataOptions", seFeDataDisabled: "seFeDataDisabled", seFeDataValueKey: "seFeDataValueKey", seFeDataSpacious: "seFeDataSpacious", seFeDataId: "seFeDataId" }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SeFeFormOptionsControlDirective),
            multi: true,
        }], queries: [{ propertyName: "template", first: true, predicate: TemplateRef, descendants: true }], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SeFeFormOptionsControlDirective, decorators: [{
            type: Directive,
            args: [{
                    providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => SeFeFormOptionsControlDirective),
                            multi: true,
                        }],
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: SeFeFormService }]; }, propDecorators: { formControlName: [{
                type: Input
            }], seFeDataOptions: [{
                type: Input
            }], seFeDataDisabled: [{
                type: Input
            }], seFeDataValueKey: [{
                type: Input
            }], seFeDataSpacious: [{
                type: Input
            }], seFeDataId: [{
                type: Input
            }], template: [{
                type: ContentChild,
                args: [TemplateRef]
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { SE_FE_FORM_FIELD_INPUT, SeFeFormControlDirective, SeFeFormControlElementDirective, SeFeFormOptionsControlDirective, SeFeFormSelectControlDirective, SeFeFormService, SeFeFormServiceModule };
